// utils/haulerEarnings.js
// Aggregations over completed jobs for Hauler-facing surfaces.
// Prefers persisted driver_pay_amt (a payout snapshot) over a
// recomputed split — the snapshot is the historical record.

import { deriveJobEconomics } from "../hooks/useJobEconomics";

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/** Best-effort payout extraction. Snapshot first, recompute fallback. */
export function jobPayout(job) {
  const persisted = Number(job?.driver_pay_amt);
  if (Number.isFinite(persisted) && persisted > 0) return persisted;
  const econ = deriveJobEconomics(job);
  return econ?.driverPay || 0;
}

/** Tier label (canonical) — falls back to raw key when undefined. */
export function jobTierLabel(job) {
  const econ = deriveJobEconomics(job);
  return econ?.tierLabel || job?.size || "—";
}

/** Duration estimate in minutes per the useJobEconomics heuristic. */
export function jobDurationMin(job) {
  const econ = deriveJobEconomics(job);
  return econ?.durationMin || 0;
}

/** Sum payout + count + avg hourly across the last 7 days. */
export function summarizeWeek(completed) {
  if (!completed || !completed.length) {
    return { total: 0, count: 0, avgHourly: 0 };
  }
  const cutoff = Date.now() - WEEK_MS;
  let total = 0;
  let count = 0;
  let durationSumMin = 0;
  for (const job of completed) {
    const t = job.completed_at ? new Date(job.completed_at).getTime() : 0;
    if (!t || t < cutoff) continue;
    const pay = jobPayout(job);
    if (pay <= 0) continue;
    total += pay;
    count += 1;
    durationSumMin += jobDurationMin(job);
  }
  const totalRounded = Math.round(total * 100) / 100;
  const avgHourly = durationSumMin > 0
    ? Math.round((total / (durationSumMin / 60)) * 100) / 100
    : 0;
  return { total: totalRounded, count, avgHourly };
}

/** Returns an array of 7 entries [{ date, label, total }] oldest -> newest. */
export function summarizeByDay(completed, todayMs = Date.now()) {
  const days = [];
  const today = new Date(todayMs);
  today.setHours(0, 0, 0, 0);
  const todayStart = today.getTime();
  for (let i = 6; i >= 0; i--) {
    const start = todayStart - i * DAY_MS;
    const d = new Date(start);
    days.push({
      start,
      end: start + DAY_MS,
      date: d,
      label: d.toLocaleDateString(undefined, { weekday: "short" }),
      total: 0,
      count: 0,
    });
  }

  for (const job of completed || []) {
    const t = job.completed_at ? new Date(job.completed_at).getTime() : 0;
    if (!t) continue;
    for (const day of days) {
      if (t >= day.start && t < day.end) {
        const pay = jobPayout(job);
        if (pay > 0) {
          day.total = Math.round((day.total + pay) * 100) / 100;
          day.count += 1;
        }
        break;
      }
    }
  }
  return days;
}
